import { useQuery } from '@tanstack/react-query';
import {
  getCheapestEstimate,
  isDomestic,
  type Rate,
} from '@util/firestore/shipengine';
import { getDefaultAddress } from '@util/firestore/users';
import { logError } from '@util/logError';
import dayjs from 'dayjs';
import type { ProductDocument } from 'models/product';
import type { UserDocument } from 'models/user';
import { useMemo } from 'react';

type UseProductShippingProps = {
  product: ProductDocument;
  userDoc: UserDocument | null | undefined;
};

export function useProductShipping({
  product,
  userDoc,
}: UseProductShippingProps) {
  const isFlatRate = product.is_flat_rate;
  const isFreeShipping =
    isFlatRate && product.shipping_costs?.every(({ cost }) => cost === 0);
  const shipTo = getDefaultAddress(userDoc);
  const MXLockerDirectID = 'E16WuBcn0UMejlp0FSdQulSSed12';
  const isMXLockerDirect = product.seller_id === MXLockerDirectID;

  const { data: shipping = null } = useQuery({
    queryKey: ['shipping', product.id, userDoc?.uid, product.shipment],
    queryFn: async () => {
      const getShipping = async () => {
        const isLoggedInAndHasAddress = !!userDoc && !!shipTo;
        if (!isLoggedInAndHasAddress) {
          return isFlatRate
            ? product.shipping_costs?.find((s) => s.code === 'US') ??
                product.shipping_costs?.[0] ??
                null
            : null;
        }

        if (!shipTo) return null;
        if (isFlatRate && product.shipping_costs?.length) {
          const foundShipping = product.shipping_costs.find(
            (s) => s.code === shipTo.country_code
          );
          if (foundShipping) return foundShipping;
          const foundOther = product.shipping_costs.find(
            (s) => s.code === 'OT'
          );
          if (foundOther) return foundOther;
          return null;
        }

        if (isDomestic(product.shipment, shipTo)) {
          return {
            cost: product.shipment.domestic_flat_rate,
            name: 'Continental US',
            code: 'US',
            estimated: true,
          };
        }

        if (shipTo.country_code && shipTo.postal_code) {
          try {
            const cheapestEstimate: Rate = await getCheapestEstimate(
              product.id,
              shipTo.postal_code,
              shipTo.country_code
            );

            return {
              cost: cheapestEstimate.total_amount,
              name: shipTo.postal_code,
              code: shipTo.country_code,
              estimated_delivery_date: cheapestEstimate.estimated_delivery_date,
            };
          } catch (e) {
            if (
              e instanceof Error &&
              e.message.includes(
                'Shipping is not available for this postal code and country'
              )
            )
              return null;
            logError(e);
            return null;
          }
        }

        return null;
      };
      return getShipping();
    },
    enabled: isFlatRate && !!userDoc && !!shipTo,
  });

  const deliveryDates = useMemo(() => {
    const addBusinessDays = (date: dayjs.Dayjs, days: number) => {
      let result = date;
      let addedDays = 0;
      while (addedDays < days) {
        result = result.add(1, 'day');
        if (result.day() !== 0 && result.day() !== 6) {
          addedDays++;
        }
      }
      return result;
    };

    const today = dayjs();

    if (shipping && 'estimated_delivery_date' in shipping) {
      return {
        earliest: dayjs(shipping.estimated_delivery_date).toISOString(),
        latest: dayjs(shipping.estimated_delivery_date)
          .add(2, 'days')
          .toISOString(),
      };
    }

    if (isFlatRate) {
      const earliest = isMXLockerDirect ? 2 : 5;
      const latest = isMXLockerDirect ? 3 : 10;
      return {
        earliest: dayjs().add(earliest, 'days').toISOString(),
        latest: dayjs().add(latest, 'days').toISOString(),
      };
    }

    return {
      earliest: addBusinessDays(today, 3).toISOString(),
      latest: addBusinessDays(today, 10).toISOString(),
    };
  }, [shipping, isFlatRate, isMXLockerDirect]);

  const today = dayjs();
  const earliestDays = Math.ceil(
    dayjs(deliveryDates.earliest).diff(today, 'day', true)
  );
  const latestDays = Math.ceil(
    dayjs(deliveryDates.latest).diff(today, 'day', true)
  );

  const deliveryRange =
    earliestDays === latestDays
      ? `${earliestDays} day${earliestDays === 1 ? '' : 's'}`
      : `${earliestDays}-${latestDays} days`;

  return {
    deliveryRange,
    earliestDays,
    latestDays,
    isFreeShipping,
    isFlatRate,
    shipping,
    isMXLockerDirect,
    shouldShow: isFlatRate && isFreeShipping,
  };
}
